import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MEdAdminSupervision = () => {
  const coreCourses = [
    { code: 'EDU 502', name: 'School Administration', credits: 3 },
    { code: 'EDU 517', name: 'Socialization and Support', credits: 3 },
    { code: 'EDU 522', name: 'Leadership, School, and the Community', credits: 3 },
    { code: 'EDU 523', name: 'School and the Curriculum', credits: 3 },
    { code: 'EDU 524', name: 'Educational Research 1', credits: 3 },
    { code: 'EDU 567', name: 'Law and Education', credits: 3 },
    { code: 'EDU 601', name: 'Educational Theory, Research, and Practice', credits: 3 },
    { code: 'EDU 615', name: 'Leadership and Management 1', credits: 3 },
    { code: 'EDU 688', name: 'Data Analysis & School Administration', credits: 3 }
  ];

  const electiveCourses = [
    { code: 'EDU 501', name: 'Review of Early Childhood Care and Education Principles and Theories', credits: 3 },
    { code: 'EDU 525', name: 'Understanding Child Development', credits: 3 },
    { code: 'EDU 520', name: 'Early Childhood Care and Education Programs', credits: 3 },
    { code: 'EDU 531', name: 'Educational Research 2', credits: 3 },
    { code: 'EDU 618', name: 'Leadership and Management 2', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Educational Leadership",
      description: "Develop advanced leadership skills for educational administration",
      icon: "👥"
    },
    {
      title: "Research-Based Practice",
      description: "Apply research methodologies to educational challenges",
      icon: "📊"
    },
    {
      title: "Legal & Policy Expertise",
      description: "Understanding educational law and policy implementation",
      icon: "⚖️"
    },
    {
      title: "Community Engagement",
      description: "Building strong school-community relationships",
      icon: "🤝"
    }
  ];

  const careerPaths = [
    {
      title: "School Administrator",
      description: "Lead educational institutions at various levels",
      icon: "🏫"
    },
    {
      title: "Educational Supervisor",
      description: "Guide and support teaching staff development",
      icon: "👨‍🏫"
    },
    {
      title: "Program Director",
      description: "Manage specialized educational programs",
      icon: "📋"
    },
    {
      title: "Policy Advisor",
      description: "Shape educational policies and procedures",
      icon: "📜"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              M.Ed. in Administration and Supervision
            </h1>
            <p className="text-xl opacity-90">
              Advanced preparation for educational leadership and administration
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                The Master of Education in Administration and Supervision program prepares 
                educators for leadership roles in educational institutions. The program 
                combines theoretical knowledge with practical applications in educational 
                leadership and administration.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (27 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Elective Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Elective Courses (Select 3 courses, 9 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {electiveCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Career Paths */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Career Opportunities</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {careerPaths.map((path, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{path.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {path.title}
                      </h3>
                      <p className="text-gray-600">{path.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Leadership Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of educational leaders and make a difference in education.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MEdAdminSupervision; 